/**
 * YouTube URL Parser, Validator, and Safe Normalizer for ADMALL INDIA.
 * Supports standard watch, short youtu.be, embed, shorts, and mobile URLs with arbitrary query parameters.
 */

const YOUTUBE_ID_REGEX = /^[a-zA-Z0-9_-]{11}$/;

export function parseYouTubeUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") {
    return {
      isValid: false,
      videoId: null,
      embedUrl: null,
      watchUrl: null,
      originalUrl: "",
      error: "No URL provided",
    };
  }

  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return {
      isValid: false,
      videoId: null,
      embedUrl: null,
      watchUrl: null,
      originalUrl: "",
      error: "URL is empty",
    };
  }

  let videoId = null;

  try {
    // Attempt standard URL parse
    const urlObj = new URL(trimmed.startsWith("http") ? trimmed : `https://${trimmed}`);
    const host = urlObj.hostname.replace(/^www\./, "").toLowerCase();

    if (host === "youtu.be") {
      // Format: https://youtu.be/VIDEO_ID
      const pathParts = urlObj.pathname.split("/").filter(Boolean);
      if (pathParts.length > 0) {
        videoId = pathParts[0];
      }
    } else if (
      host === "youtube.com" ||
      host === "m.youtube.com" ||
      host === "youtube-nocookie.com"
    ) {
      if (urlObj.pathname === "/watch") {
        // Format: https://www.youtube.com/watch?v=VIDEO_ID
        videoId = urlObj.searchParams.get("v");
      } else if (urlObj.pathname.startsWith("/embed/")) {
        // Format: https://www.youtube.com/embed/VIDEO_ID
        videoId = urlObj.pathname.replace("/embed/", "").split("/")[0].split("?")[0];
      } else if (urlObj.pathname.startsWith("/shorts/")) {
        // Format: https://www.youtube.com/shorts/VIDEO_ID
        videoId = urlObj.pathname.replace("/shorts/", "").split("/")[0].split("?")[0];
      } else if (urlObj.pathname.startsWith("/v/")) {
        // Legacy: https://www.youtube.com/v/VIDEO_ID
        videoId = urlObj.pathname.replace("/v/", "").split("/")[0].split("?")[0];
      }
    }
  } catch {
    // Regex fallback for partially malformed strings
    const match = trimmed.match(
      /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/))([a-zA-Z0-9_-]{11})/
    );
    if (match && match[1]) {
      videoId = match[1];
    }
  }

  if (videoId && YOUTUBE_ID_REGEX.test(videoId)) {
    return {
      isValid: true,
      videoId,
      embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`,
      watchUrl: `https://www.youtube.com/watch?v=${videoId}`,
      originalUrl: trimmed,
      error: null,
    };
  }

  return {
    isValid: false,
    videoId: null,
    embedUrl: null,
    watchUrl: null,
    originalUrl: trimmed,
    error: "Invalid YouTube URL format. Expected youtube.com/watch?v=..., youtu.be/..., or youtube.com/shorts/...",
  };
}

export function isDirectVideoUrl(url) {
  if (!url || typeof url !== "string") return false;
  const lower = url.trim().toLowerCase();
  return (
    lower.endsWith(".mp4") ||
    lower.endsWith(".webm") ||
    lower.endsWith(".mov") ||
    lower.endsWith(".m4v") ||
    lower.includes("/api/files/") ||
    lower.startsWith("blob:")
  );
}
